import { useEffect, useRef } from 'react';
import { GAME_DB } from '../../db/game';
import { getGameState, sendTaskAnswer, sendTaskAnswersList, sendTaskAnswersTimeLogsList, } from '../../api/game-mode/game';
import { GAME_END_TIME } from '../../app.config.vars/stored';
import { setIsModalOpen, setModalContent, setIsSyncing, setSyncProgress, } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
import { useAppDispatch } from '../../store';
const BASE_SYNC_INTERVAL = 10000;
const MAX_SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_CONSECUTIVE_FAILURES = 10;
export const useDataSync = () => {
    const dispatch = useAppDispatch();
    const isSyncingRef = useRef(false);
    const consecutiveFailuresRef = useRef(0);
    const intervalRef = useRef(null);
    const getSyncInterval = () => {
        const failures = consecutiveFailuresRef.current;
        if (failures === 0)
            return BASE_SYNC_INTERVAL;
        return Math.min(BASE_SYNC_INTERVAL * Math.pow(2, failures), MAX_SYNC_INTERVAL);
    };
    const restartInterval = (syncFn) => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(syncFn, getSyncInterval());
    };
    useEffect(() => {
        const syncIfOnline = async () => {
            if (!navigator.onLine)
                return;
            if (location.href.includes('admin'))
                return;
            // Fix 1: Concurrency lock — skip if already syncing
            if (isSyncingRef.current)
                return;
            isSyncingRef.current = true;
            try {
                const localTaskAnswers = await GAME_DB.GET.localTaskAnswers();
                const localAnswersTimeLogs = await GAME_DB.GET.localAnswersTimeLogsList();
                if (localTaskAnswers?.length || localAnswersTimeLogs?.length) {
                    dispatch(setIsSyncing(true));
                    let hasFailure = false;
                    // Calculate total sync items for progress tracking
                    const answerCount = localTaskAnswers?.length || 0;
                    const timeLogCount = localAnswersTimeLogs?.length ? 1 : 0;
                    const totalItems = answerCount + timeLogCount;
                    let completedItems = 0;
                    dispatch(setSyncProgress({ completed: 0, total: totalItems }));
                    if (localTaskAnswers?.length) {
                        // Partition answers: uploads are sent individually to avoid large batch payloads
                        const uploadAnswers = [];
                        const nonUploadAnswers = [];
                        for (const answer of localTaskAnswers) {
                            if (answer.answerType === 'upload') {
                                uploadAnswers.push(answer);
                            }
                            else {
                                nonUploadAnswers.push(answer);
                            }
                        }
                        // Send non-upload answers as a batch (lightweight JSON)
                        if (nonUploadAnswers.length) {
                            const response = await sendTaskAnswersList({ answers: nonUploadAnswers });
                            if (!response.error) {
                                const syncedTaskIds = nonUploadAnswers.map((a) => a.task);
                                await GAME_DB.UPDATE.clearLocalAnswersByTaskIds(syncedTaskIds);
                                completedItems += nonUploadAnswers.length;
                                dispatch(setSyncProgress({ completed: completedItems, total: totalItems }));
                            }
                            else {
                                hasFailure = true;
                            }
                        }
                        // Send upload answers one at a time to avoid PHP-FPM timeouts.
                        // Note: we deliberately do NOT clear the Blob from the uploads
                        // store here — keeping it around lets the user re-open a completed
                        // task and still see the photo preview. The store is cleared in
                        // bulk by GAME_DB.RESET.game() when a new game starts.
                        for (const uploadAnswer of uploadAnswers) {
                            const response = await sendTaskAnswer(uploadAnswer);
                            if (!response.error) {
                                await GAME_DB.UPDATE.clearLocalAnswersByTaskIds([uploadAnswer.task]);
                                completedItems += 1;
                                dispatch(setSyncProgress({ completed: completedItems, total: totalItems }));
                            }
                            else {
                                hasFailure = true;
                            }
                        }
                    }
                    if (localAnswersTimeLogs?.length) {
                        const response = await sendTaskAnswersTimeLogsList({
                            timeLogs: localAnswersTimeLogs,
                        });
                        if (!response.error) {
                            await GAME_DB.UPDATE.clearLocalAnswersTimeLogs();
                            await getGameState();
                            completedItems += 1;
                            dispatch(setSyncProgress({ completed: completedItems, total: totalItems }));
                        }
                        else {
                            hasFailure = true;
                        }
                    }
                    // Fix 2: Track consecutive failures for exponential backoff
                    if (hasFailure) {
                        consecutiveFailuresRef.current += 1;
                        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                            console.warn(`Data sync: stopped retrying after ${MAX_CONSECUTIVE_FAILURES} consecutive failures`);
                            if (intervalRef.current) {
                                clearInterval(intervalRef.current);
                                intervalRef.current = null;
                            }
                        }
                        else {
                            // Increase interval with exponential backoff
                            restartInterval(syncIfOnline);
                        }
                    }
                    else {
                        // Reset on success
                        consecutiveFailuresRef.current = 0;
                        restartInterval(syncIfOnline);
                    }
                    dispatch(setIsSyncing(false));
                }
                if (!location.pathname.includes('game-result') &&
                    !location.pathname.includes('gameplay')) {
                    const unsavedGame = !!localStorage.getItem(GAME_END_TIME);
                    if (unsavedGame) {
                        dispatch(setIsModalOpen(true));
                        dispatch(setModalContent(MODAL_CONTENT.saveGameResult.key));
                    }
                }
            }
            catch (error) {
                console.log('Nothing to synchronize. Local DB is not initialize jet');
                dispatch(setIsSyncing(false));
                consecutiveFailuresRef.current += 1;
                if (consecutiveFailuresRef.current < MAX_CONSECUTIVE_FAILURES) {
                    restartInterval(syncIfOnline);
                }
            }
            finally {
                isSyncingRef.current = false;
            }
        };
        window.addEventListener('online', syncIfOnline);
        intervalRef.current = setInterval(syncIfOnline, BASE_SYNC_INTERVAL);
        syncIfOnline();
        return () => {
            window.removeEventListener('online', syncIfOnline);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);
};
